import { useState, useEffect, useCallback } from 'react'

/**
 * AP-17-E — geteilt von ERP und Stempel-PWA; die Logik war zweimal Zeile für
 * Zeile dieselbe.
 *
 * Fängt das `beforeinstallprompt`-Event des Browsers ab und stellt es
 * als aufrufbare Funktion bereit (A2HS – Add to Home Screen / Installieren).
 *
 * Rückgabe:
 *  - `canInstall`   – true wenn der Browser den nativen Ein-Klick-Install anbietet
 *                     (Chrome/Edge/Android). Auf iOS/Firefox immer false.
 *  - `install`      – ruft den nativen Installations-Dialog auf
 *  - `isInstalled`  – true wenn die App bereits im Standalone-Modus läuft
 *  - `platform`     – erkannte Plattform, um den passenden Fallback (geführte
 *                     Anleitung) zu zeigen wenn `canInstall` false ist (iOS).
 */

export type InstallPlatform = 'ios' | 'android' | 'other'

/** Erkennt die Plattform für die passende Anleitung. iPadOS 13+ meldet sich als
 *  „Macintosh" → zusätzlich auf Touch prüfen. */
function detectPlatform(): InstallPlatform {
  if (typeof navigator === 'undefined') return 'other'
  const ua = navigator.userAgent
  const isIOS =
    /iPhone|iPad|iPod/i.test(ua) ||
    (/Macintosh/i.test(ua) && typeof document !== 'undefined' && 'ontouchend' in document)
  if (isIOS) return 'ios'
  if (/Android/i.test(ua)) return 'android'
  return 'other'
}

interface BeforeInstallPromptEvent extends Event {
  readonly platforms: string[]
  readonly userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>
  prompt(): Promise<void>
}

export function useInstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null)
  const [isInstalled, setIsInstalled] = useState(false)
  const [platform] = useState<InstallPlatform>(detectPlatform)

  useEffect(() => {
    const mq = window.matchMedia('(display-mode: standalone)')
    setIsInstalled(mq.matches)

    const handleChange = (e: MediaQueryListEvent) => setIsInstalled(e.matches)
    mq.addEventListener('change', handleChange)

    const handleBeforeInstall = (e: Event) => {
      e.preventDefault()
      setDeferredPrompt(e as BeforeInstallPromptEvent)
    }
    window.addEventListener('beforeinstallprompt', handleBeforeInstall)

    const handleAppInstalled = () => {
      setDeferredPrompt(null)
      setIsInstalled(true)
    }
    window.addEventListener('appinstalled', handleAppInstalled)

    return () => {
      mq.removeEventListener('change', handleChange)
      window.removeEventListener('beforeinstallprompt', handleBeforeInstall)
      window.removeEventListener('appinstalled', handleAppInstalled)
    }
  }, [])

  const install = useCallback(async () => {
    if (!deferredPrompt) return
    await deferredPrompt.prompt()
    const { outcome } = await deferredPrompt.userChoice
    if (outcome === 'accepted') {
      setDeferredPrompt(null)
    }
  }, [deferredPrompt])

  return {
    canInstall: deferredPrompt !== null,
    install,
    isInstalled,
    platform,
  }
}
